import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { getIcon, type IconName } from "@sylviq/ui-react-web/extensions/icons";
import { Link, useParams } from "@tanstack/react-router";
import { Check, Clock, Hammer } from "lucide-react";
import { SiteLayout } from "@/features/marketing/SiteLayout";
import { useDocumentMeta } from "@/lib/document-meta";
import {
  FEATURE_STATUS_LABEL,
  type FeatureStatus,
  findProduct,
  PRODUCTS,
  type ProductStatus,
  STATUS_LABEL,
} from "./catalog";

const STATUS_TONE: Record<ProductStatus, "success" | "warning" | "neutral"> = {
  live: "success",
  "in-development": "warning",
  planned: "neutral",
};

function FeatureMark({ status }: { status: FeatureStatus }) {
  const Icon =
    status === "available" ? Check : status === "finishing" ? Hammer : Clock;
  return (
    <span
      className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded-full ${
        status === "available"
          ? "bg-[var(--action)] text-[var(--action-foreground)]"
          : "border border-[var(--border)] text-[var(--muted-foreground)]"
      }`}
    >
      <Icon className="size-3" aria-hidden="true" />
      <span className="sr-only">{FEATURE_STATUS_LABEL[status]}: </span>
    </span>
  );
}

export function ProductsIndexPage() {
  useDocumentMeta({
    title: "Products — accounting, inventory, CRM and more | SYLVIQ",
    description:
      "Everything on SYLVIQ: Accounting (live today), inventory and operations, CRM and sales, and the shared organization and admin layer.",
    path: "/products",
  });
  return (
    <SiteLayout>
      <section className="mx-auto w-full max-w-6xl px-4 pt-16 pb-8 sm:px-6 sm:pt-20">
        <p className="text-xs font-semibold tracking-widest text-[var(--muted-foreground)] uppercase">
          Products
        </p>
        <h1 className="mt-3 max-w-3xl text-4xl font-semibold tracking-tight text-balance sm:text-5xl">
          One account, every app you need
        </h1>
        <p className="mt-4 max-w-2xl text-base text-pretty text-[var(--muted-foreground)] sm:text-lg">
          Install what you use and skip the rest. Everything shares one sign-in,
          one customer record and one set of permissions.
        </p>
      </section>
      <section
        aria-label="All products"
        className="mx-auto w-full max-w-6xl px-4 pb-8 sm:px-6"
      >
        <ul className="grid list-none gap-4 p-0 sm:grid-cols-2">
          {PRODUCTS.map((product) => {
            const Icon = getIcon(product.icon as IconName);
            return (
              <li key={product.slug}>
                <Link
                  to="/products/$slug"
                  params={{ slug: product.slug }}
                  className="surface group flex h-full flex-col gap-4 rounded-2xl border p-6 transition hover:border-[var(--action)]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="grid size-11 place-items-center rounded-xl bg-[var(--action)] text-[var(--action-foreground)]">
                      <Icon className="size-5" aria-hidden="true" />
                    </span>
                    <Badge tone={STATUS_TONE[product.status]}>
                      {STATUS_LABEL[product.status]}
                    </Badge>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">{product.name}</h2>
                    <p className="mt-1.5 text-sm text-[var(--muted-foreground)]">
                      {product.tagline}
                    </p>
                  </div>
                  <span className="mt-auto text-sm font-medium text-[var(--action)] group-hover:underline">
                    See what it does →
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>
    </SiteLayout>
  );
}

export function ProductPage() {
  const { slug } = useParams({ strict: false }) as { slug?: string };
  const product = slug ? findProduct(slug) : undefined;
  useDocumentMeta({
    title: product?.seoTitle ?? "Product not found | SYLVIQ",
    description: product?.seoDescription ?? "This product page does not exist.",
    path: product ? `/products/${product.slug}` : "/products",
  });

  if (!product) {
    return (
      <SiteLayout>
        <section className="mx-auto w-full max-w-2xl px-4 py-24 text-center sm:px-6">
          <h1 className="text-3xl font-semibold">Product not found</h1>
          <p className="mt-3 text-[var(--muted-foreground)]">
            We don't have a product at that address.
          </p>
          <Button asChild className="mt-6 rounded-full">
            <Link to="/products">See all products</Link>
          </Button>
        </section>
      </SiteLayout>
    );
  }

  const Icon = getIcon(product.icon as IconName);
  const others = PRODUCTS.filter((item) => item.slug !== product.slug);

  return (
    <SiteLayout>
      <section className="mx-auto w-full max-w-6xl px-4 pt-14 pb-10 sm:px-6 sm:pt-20">
        <nav
          aria-label="Breadcrumb"
          className="text-sm text-[var(--muted-foreground)]"
        >
          <Link to="/products" className="hover:underline">
            Products
          </Link>{" "}
          / <span className="text-[var(--foreground)]">{product.name}</span>
        </nav>
        <div className="mt-6 flex items-center gap-4">
          <span className="grid size-14 place-items-center rounded-2xl bg-[var(--action)] text-[var(--action-foreground)]">
            <Icon className="size-7" aria-hidden="true" />
          </span>
          <Badge tone={STATUS_TONE[product.status]}>
            {STATUS_LABEL[product.status]}
          </Badge>
        </div>
        <h1 className="mt-5 max-w-3xl text-4xl font-semibold tracking-tight text-balance sm:text-5xl">
          {product.name}
        </h1>
        <p className="mt-4 max-w-2xl text-base text-pretty text-[var(--muted-foreground)] sm:text-lg">
          {product.summary}
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          {product.status === "live" ? (
            <Button asChild size="lg" className="rounded-full">
              <Link to="/signup">Get started</Link>
            </Button>
          ) : (
            <Button asChild size="lg" className="rounded-full">
              <a href="/#early-access">Join the waitlist</a>
            </Button>
          )}
          <Button asChild size="lg" variant="outline" className="rounded-full">
            <Link to="/products">All products</Link>
          </Button>
        </div>
      </section>

      <section
        aria-label={`${product.name} features`}
        className="mx-auto w-full max-w-6xl px-4 pb-8 sm:px-6"
      >
        <div className="grid gap-4 md:grid-cols-2">
          {product.groups.map((group) => (
            <div key={group.title} className="surface rounded-2xl border p-6">
              <h2 className="text-lg font-semibold">{group.title}</h2>
              <ul className="mt-4 grid list-none gap-3 p-0">
                {group.features.map((feature) => (
                  <li
                    key={feature.name}
                    className="flex items-start gap-3 text-sm"
                  >
                    <FeatureMark status={feature.status} />
                    <span
                      className={
                        feature.status === "planned"
                          ? "text-[var(--muted-foreground)]"
                          : undefined
                      }
                    >
                      {feature.name}
                      {feature.status !== "available" ? (
                        <span className="ml-2 text-xs text-[var(--muted-foreground)]">
                          ({FEATURE_STATUS_LABEL[feature.status]})
                        </span>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-[var(--muted-foreground)]">
          Available = built and tested. Finishing = built, being polished.
          Planned = designed, not built yet.
        </p>
      </section>

      <section
        aria-label="More products"
        className="mx-auto w-full max-w-6xl px-4 pt-10 sm:px-6"
      >
        <h2 className="text-lg font-semibold">More on SYLVIQ</h2>
        <ul className="mt-4 flex list-none flex-wrap gap-2 p-0">
          {others.map((item) => (
            <li key={item.slug}>
              <Button
                asChild
                size="sm"
                variant="outline"
                className="rounded-full"
              >
                <Link to="/products/$slug" params={{ slug: item.slug }}>
                  {item.name}
                </Link>
              </Button>
            </li>
          ))}
        </ul>
      </section>
    </SiteLayout>
  );
}
